import { useContext, useMemo, useState, useEffect, useRef } from "react";
import { useQueries } from "@tanstack/react-query";
import { AppContext } from "../context/app.context";
import { ModelCxContext } from "../context/modelCx.context";
import { resolveBackendMode } from "../utility/backendConfig";
import { refs } from "../convex/apiRefs";
import { convexReadResult, CX_SOURCE } from "../convex/convexRead";
import { decodeConvexList } from "../convex/decodeConvexDoc";
import {
  countSettledLocations,
  computeNextReleased,
} from "../utility/dashboardThrottle";

import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
  MONGODB_DATASOURCE_TS,
  MONGODB_COLLECTION_CANDIDATES,
  MONGODB_COLLECTION_ATTRIBUTES,
  MONGODB_COLLECTION_ACTIVEQUEUE,
  MONGODB_MAXLIMIT_CANDIDATESBYCOMPANY,
  MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION,
  MONGODB_MAXLIMIT_PROJECTSBYCOMPANY,
} from "../utility/keys";

// #191 gap1 — convexOnly: fetch a dashboard location's 3 reads from Convex
// (never the Lambda), returning the same { message: { result } } shape the
// Lambda fetch returns so buildDashboardOpenings parses them unchanged.
async function fetchDashboardConvex(method, companyCode, locationCode) {
  const source = CX_SOURCE;
  if (method === "getProjectsByCompanyWithShared") {
    const raw = await convexReadResult(
      refs.getProjectsByCompanyWithShared,
      { source, companyCode, location: locationCode },
      "raw"
    );
    const result = { projects: decodeConvexList(raw?.projects || []), meta: raw?.meta };
    return { message: { result }, _convexOnly: true };
  }
  const ref =
    method === "getCandidatesByCompany"
      ? refs.getCandidatesByCompany
      : refs.getAttributesByLocation;
  const result = await convexReadResult(
    ref,
    { source, companyCode, location: locationCode },
    "list"
  );
  return { message: { result }, _convexOnly: true };
}

// - - - - - - - - - -
// #139 Phase 6 — Next Day Openings Dashboard: multi-location parallel fetch.
//
// Fires three TanStack queries per selected location, in parallel via
// `useQueries`. Each location ends up with { candidates, attributes,
// projects } when its 3 calls all resolve. The hook returns a per-location
// map plus aggregate isLoading / isError flags.
//
// --------------------------------------------------------------------------
// QUERY CHOICES
// --------------------------------------------------------------------------
//
//   getCandidatesByCompany    — installers (rows) at the location
//   getAttributesByLocation   — OFF, compliance hold, custom-title markers
//                                 for any visible date (we'll filter to
//                                 tomorrow downstream)
//   getProjectsByCompany       — scheduled work, so we can detect any installer
//                                 already booked tomorrow (statusCode 4)
//
// These mirror the same three calls the Optimizer fires for its current
// locationFilter — but we use a different queryKey prefix ("dashboard:")
// so:
//   1. We don't inherit the Optimizer's polling intervals (the dashboard
//      is meant to be a snapshot, not a live view).
//   2. The Optimizer's side-effect-laden queryFns (timing logs, mongoDB
//      Logs Collection writes, etc.) don't double-fire.
// Trade-off: we don't share cache with the Optimizer. If the user has the
// dashboard's BEN data loaded AND the Optimizer is also pointed at BEN,
// each side makes its own call. Acceptable given polling differences.
//
// --------------------------------------------------------------------------
// USING `getProjectsByCompanyWithShared` (NOT `getProjectsByCompany`)
// --------------------------------------------------------------------------
//
// #161 (2026-05-26, Brandon) flipped this design. Originally this hook used
// the strict-home-location `getProjectsByCompany` query and noted that
// shared-installer cross-location accommodation was a separate design
// consideration. PROD Time Machine forensic for invoice 2498626 (1990.01,
// home ATL, statusCode 7 multi-day Carpet job at AUG, 5/26–5/27)
// confirmed the gap: the dashboard listed 1990.01 as an opening at ATL
// while the Optimizer correctly rendered "SHARED AUG" for that cell.
//
// `getProjectsByCompanyWithShared` returns:
//   • All projects at locationFilter (regardless of installer)
//   • All projects at OTHER locations for installers whose home is
//     locationFilter AND who have Additional_Locations set
// The latter set is exactly what makes a "SHARED" tile appear in the
// Optimizer's home-location grid. Including those in the dashboard's
// scheduleArray means shouldHighlightEmptyTomorrowCell's hasJob check
// (location-filter-dropped per #138) correctly suppresses the opening
// for shared installers booked elsewhere on tomorrow.
//
// `extractProjects` already handles the {projects, meta} response shape
// returned by this variant — see buildDashboardOpenings.js.
//
// --------------------------------------------------------------------------
// staleTime / refetchOnWindowFocus
// --------------------------------------------------------------------------
//
// staleTime: 60_000 — modal-open within 60s of a prior open hits cache
// (e.g., user closes and reopens to re-check). After 60s the data is
// considered stale and a fresh fetch happens. Manual refresh button (TBD
// in Phase 7) will explicitly invalidate.
//
// refetchOnWindowFocus: false — opening the dashboard then alt-tabbing to
// Slack and back should not re-fire 3N API calls. Tab-focus is not a
// signal that we want fresh data; the modal-open IS that signal.

async function fetchDashboardLambda(body, label) {
  const response = await fetch(MONGODB_AWSGW_QUERIES, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": MONGODB_AWSGW_APIKEY,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(
      `useDashboardOpenings ${label} HTTP ${response.status}: ${response.statusText}`
    );
  }
  const result = await response.json();
  if (result.errorType || result.errorMessage) {
    const errorMsg =
      result.errorMessage || result.errorType || "Unknown error";
    throw new Error(`useDashboardOpenings ${label}: ${errorMsg}`);
  }
  return result;
}

function buildCandidatesBody(companyCode, locationCode, mongoDatabase) {
  return {
    method: "getCandidatesByCompany",
    dataSource: MONGODB_DATASOURCE_TS,
    database: mongoDatabase,
    collection: MONGODB_COLLECTION_CANDIDATES,
    companyCode,
    locationFilter: locationCode,
    maxLimit: MONGODB_MAXLIMIT_CANDIDATESBYCOMPANY,
  };
}

function buildAttributesBody(companyCode, locationCode, mongoDatabase) {
  return {
    method: "getAttributesByLocation",
    dataSource: MONGODB_DATASOURCE_TS,
    database: mongoDatabase,
    collection: MONGODB_COLLECTION_ATTRIBUTES,
    companyCode,
    locationFilter: locationCode,
    maxLimit: MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION,
  };
}

function buildProjectsBody(companyCode, locationCode, mongoDatabase) {
  // #161 (2026-05-26, Brandon) — switched from getProjectsByCompany to
  // getProjectsByCompanyWithShared so the dashboard sees cross-location
  // jobs assigned to shared installers (e.g., 1990.01 home=ATL with a
  // statusCode-7 job at AUG covering tomorrow → must NOT show as opening
  // at ATL). See file-top header for full root-cause writeup.
  return {
    method: "getProjectsByCompanyWithShared",
    dataSource: MONGODB_DATASOURCE_TS,
    database: mongoDatabase,
    collection: MONGODB_COLLECTION_ACTIVEQUEUE,
    companyCode,
    locationFilter: locationCode,
    maxLimit: MONGODB_MAXLIMIT_PROJECTSBYCOMPANY,
  };
}

/**
 * #139 Phase 6 — Multi-location dashboard fetch.
 *
 * @param {Object}   params
 * @param {string}   params.companyCode        — "KI" or "SCG"; current user's company
 * @param {string[]} params.selectedLocations  — array of location codes to fetch
 * @param {boolean}  params.enabled            — gate; pass false when the modal isn't open
 * @returns {{
 *   dataByLocation: Record<string, {
 *     candidates: unknown,
 *     attributes: unknown,
 *     projects:   unknown,
 *     isLoading:  boolean,
 *     isError:    boolean,
 *     error:      Error | null,
 *   }>,
 *   isLoading: boolean,    // true while ANY of the queries is still in-flight
 *   isError:   boolean,    // true if ANY of the queries failed
 *   isReady:   boolean,    // shortcut: !isLoading && !isError && selectedLocations.length > 0
 * }}
 */
export function useDashboardOpenings({
  companyCode,
  selectedLocations,
  enabled = true,
}) {
  const { appEnvirons } = useContext(AppContext);
  const { tsUserDocument } = useContext(ModelCxContext);
  const isConvexOnly = resolveBackendMode(tsUserDocument) === "convexOnly";
  const mongoDatabase = appEnvirons?.mongoDb;

  // Build a flat queries array. Order is deterministic: each location gets
  // exactly 3 entries (candidates, attributes, projects) in that order.
  const safeLocations = useMemo(
    () => (Array.isArray(selectedLocations) ? selectedLocations : []),
    [selectedLocations]
  );

  // #168 — concurrency throttle.
  //
  // Firing 3 queries × N locations all at once (153 for "all regions")
  // overwhelms API Gateway: getProjectsByCompanyWithShared starts erroring
  // internally and returns its silent { projects: [], meta:{error} } sentinel,
  // which inflates the openings count with phantom installers. We bound the
  // in-flight set to CONCURRENCY locations at a time and release more as
  // earlier locations settle, so the fan-out never storms the gateway.
  //
  // releasedCount is the number of locations (in array order) currently
  // allowed to fetch. It starts at CONCURRENCY, resets to CONCURRENCY each
  // time the selected-location SET changes, and grows monotonically as
  // locations settle (success OR error).
  const CONCURRENCY = 6;
  const [releasedCount, setReleasedCount] = useState(CONCURRENCY);
  const locationsKey = safeLocations.join("|");
  const prevKeyRef = useRef(locationsKey);

  const queries = useMemo(() => {
    if (!enabled || !companyCode || (!isConvexOnly && !mongoDatabase)) return [];
    const baseEnabled = !!companyCode && (isConvexOnly || !!mongoDatabase);
    return safeLocations.flatMap((locationCode, locIndex) => {
      // Throttle gate: a location's 3 queries are only enabled once its
      // index falls within the released window. Disabled queries sit idle
      // (no network call) until released.
      const isEnabledLoc =
        baseEnabled && !!locationCode && locIndex < releasedCount;
      return [
        {
          queryKey: [
            "dashboard:getCandidatesByCompany",
            companyCode,
            locationCode,
          ],
          queryFn: () =>
            isConvexOnly
              ? fetchDashboardConvex("getCandidatesByCompany", companyCode, locationCode)
              : fetchDashboardLambda(
                  buildCandidatesBody(companyCode, locationCode, mongoDatabase),
                  `candidates@${locationCode}`
                ),
          enabled: isEnabledLoc,
          staleTime: 60_000,
          refetchOnWindowFocus: false,
          // Each modal close+reopen unmounts/remounts DashboardModal,
          // which triggers a fresh fetch when refetchOnMount === "always"
          // (regardless of staleTime). Matches the user's mental model:
          // close + reopen = manual refresh.
          refetchOnMount: "always",
        },
        {
          queryKey: [
            "dashboard:getAttributesByLocation",
            companyCode,
            locationCode,
          ],
          queryFn: () =>
            isConvexOnly
              ? fetchDashboardConvex("getAttributesByLocation", companyCode, locationCode)
              : fetchDashboardLambda(
                  buildAttributesBody(companyCode, locationCode, mongoDatabase),
                  `attributes@${locationCode}`
                ),
          enabled: isEnabledLoc,
          staleTime: 60_000,
          refetchOnWindowFocus: false,
          // Each modal close+reopen unmounts/remounts DashboardModal,
          // which triggers a fresh fetch when refetchOnMount === "always"
          // (regardless of staleTime). Matches the user's mental model:
          // close + reopen = manual refresh.
          refetchOnMount: "always",
        },
        {
          // #161: queryKey name updated to reflect the new method so any
          // cached entries from the prior version are not reused.
          queryKey: [
            "dashboard:getProjectsByCompanyWithShared",
            companyCode,
            locationCode,
          ],
          queryFn: () =>
            isConvexOnly
              ? fetchDashboardConvex("getProjectsByCompanyWithShared", companyCode, locationCode)
              : fetchDashboardLambda(
                  buildProjectsBody(companyCode, locationCode, mongoDatabase),
                  `projects@${locationCode}`
                ),
          enabled: isEnabledLoc,
          staleTime: 60_000,
          refetchOnWindowFocus: false,
          refetchOnMount: "always",
        },
      ];
    });
  }, [companyCode, mongoDatabase, safeLocations, enabled, releasedCount, isConvexOnly]);

  const results = useQueries({ queries });

  // How many locations have fully settled (all 3 queries success OR error).
  // A disabled (not-yet-released) query is `pending`/`idle` — NEITHER success
  // nor error — so it is correctly NOT counted as settled, which is what makes
  // the release window advance only on real completion. (Pure helper, tested.)
  const completedLocationCount = useMemo(
    () => countSettledLocations(results, safeLocations.length),
    [results, safeLocations.length]
  );

  // Reset the window when the location set changes; otherwise grow it as
  // locations settle. computeNextReleased is monotonic-between-resets and the
  // functional setState only commits real changes, so this converges instead
  // of thrashing.
  useEffect(() => {
    const keyChanged = prevKeyRef.current !== locationsKey;
    if (keyChanged) prevKeyRef.current = locationsKey;
    setReleasedCount((prev) =>
      computeNextReleased({
        keyChanged,
        prevReleased: prev,
        completedLocationCount,
        totalLocations: safeLocations.length,
        concurrency: CONCURRENCY,
      })
    );
  }, [locationsKey, completedLocationCount, safeLocations.length]);

  const dataByLocation = useMemo(() => {
    const map = {};
    safeLocations.forEach((locationCode, i) => {
      const candidatesResult = results[i * 3 + 0] || {};
      const attributesResult = results[i * 3 + 1] || {};
      const projectsResult = results[i * 3 + 2] || {};
      map[locationCode] = {
        candidates: candidatesResult.data,
        attributes: attributesResult.data,
        projects: projectsResult.data,
        // isLoading: ANY of the 3 queries is on its first-ever fetch
        // (no data yet). Stays false on refetches; use isFetching for
        // those.
        isLoading: !!(
          candidatesResult.isLoading ||
          attributesResult.isLoading ||
          projectsResult.isLoading
        ),
        // isFetching: ANY of the 3 queries is in flight — initial OR
        // refetch. Used to show "refreshing…" on the per-location row
        // when the user clicks ↻ Refresh or close-reopens the modal.
        isFetching: !!(
          candidatesResult.isFetching ||
          attributesResult.isFetching ||
          projectsResult.isFetching
        ),
        isError: !!(
          candidatesResult.isError ||
          attributesResult.isError ||
          projectsResult.isError
        ),
        error:
          candidatesResult.error ||
          attributesResult.error ||
          projectsResult.error ||
          null,
      };
    });
    return map;
  }, [results, safeLocations]);

  // isLoading: TRUE only during the FIRST fetch (no data yet). Stays
  //   false on every subsequent refetch. Used for the "have we ever
  //   loaded?" gate.
  // isFetching: TRUE whenever ANY fetch is in flight — initial OR
  //   refetch. Used for the visible loading badge + refresh-button
  //   disabled state so the user gets feedback when they click Refresh
  //   or when close-reopen triggers a refetch-on-mount.
  const isLoading = useMemo(
    () => results.some((r) => r && r.isLoading),
    [results]
  );
  const isFetching = useMemo(
    () => results.some((r) => r && r.isFetching),
    [results]
  );
  const isError = useMemo(() => results.some((r) => r && r.isError), [results]);
  const isReady =
    !isLoading && !isFetching && !isError && safeLocations.length > 0;

  // #168 — with the throttle, queries are released in waves, so there are
  // brief gaps between waves where nothing is in flight (isFetching false)
  // even though later locations haven't fetched yet. isReady would flicker
  // true in those gaps. `allSettled` is the real "every selected location is
  // fully done" signal — used to gate the headline total's partial state so
  // a mid-load number is never presented as final.
  const allSettled =
    safeLocations.length > 0 &&
    completedLocationCount === safeLocations.length;

  // Oldest `dataUpdatedAt` across all queries that have fetched at least
  // once. Represents the staleness FLOOR — i.e. "no piece of the data
  // you're looking at is fresher than this." TanStack assigns 0 to
  // queries that haven't completed yet, so filter those out.
  const dataUpdatedAt = useMemo(() => {
    const times = results
      .map((r) => (r && typeof r.dataUpdatedAt === "number" ? r.dataUpdatedAt : 0))
      .filter((t) => t > 0);
    if (times.length === 0) return null;
    return Math.min(...times);
  }, [results]);

  // Manual refresh — re-fetches every query, but in CONCURRENCY-sized waves
  // rather than all at once. #168: the old all-at-once Promise.all re-created
  // the same 153-call storm that makes getProjectsByCompanyWithShared error
  // and return its silent-empty sentinel — which is exactly what made the
  // count swing on every refresh. Batching the refetch keeps the gateway
  // load bounded the same way the initial release window does.
  const refetchAll = async () => {
    const BATCH = CONCURRENCY * 3; // 3 queries per location
    for (let i = 0; i < results.length; i += BATCH) {
      const wave = results.slice(i, i + BATCH);
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(wave.map((r) => r?.refetch?.()));
    }
  };

  return {
    dataByLocation,
    isLoading,
    isFetching,
    isError,
    isReady,
    allSettled,
    dataUpdatedAt,
    refetchAll,
  };
}
